import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams } from "react-router-dom";

type SelectTripSkeletonProps = {
  departureCount: number;
  returnCount: number;
};

function TripCardSkeleton() {
  return (
    <div className="rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-24 rounded-md" />
        <Skeleton className="h-4 w-20 rounded-md" />
      </div>
    </div>
  );
}

function TripGroupSkeleton({ count }: { title: string; count: number }) {
  if (count === 0) return null;

  return (
    <section className="mt-8">
      <Skeleton className="h-6 w-44 rounded-md" />

      <div className="mt-2 flex flex-col gap-3">
        {Array.from({ length: count }).map((_, index) => (
          <TripCardSkeleton key={index} />
        ))}
      </div>
    </section>
  );
}

export default function SelectTripSkeleton({
  departureCount,
  returnCount,
}: SelectTripSkeletonProps) {
  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("tripType") === "return";

  return (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <section className="mt-2 rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
            <Skeleton className="h-3 w-44" />

            <div className="mt-4 flex items-stretch gap-4">
              <div className="flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-32 mt-2" />
              </div>

              {isReturn && (
                <>
                  <Separator orientation="vertical" className="h-auto" />
                  <div className="flex-1">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-32 mt-2" />
                  </div>
                </>
              )}
            </div>

            <Skeleton className="h-3 w-32 mt-4" />
            <Skeleton className="h-12 w-full rounded-xl mt-4" />
          </section>
          <TripGroupSkeleton
            title="Select Departure Trip"
            count={departureCount}
          />

          <TripGroupSkeleton title="Select Return Trip" count={returnCount} />
        </div>

        <div className="hidden md:block">
          <div className="rounded-2xl bg-card px-4 mt-2 py-6 shadow-xl">
            <Skeleton className="h-4 w-24 mb-3" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </div>
        </div>
      </div>

      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-12 w-24 rounded-xl" />
        </div>
      </footer>
    </>
  );
}
